/**
 * Billing Page Template
 *
 * Pure presentation component for billing/subscription page
 * Displays current plan, usage/credits, and available plans for upgrade
 *
 * REFACTORED with Facade Pattern!
 *
 * Before: 15+ individual props (user, sidebar, plan data, handlers...)
 * After: 1 facade + 2 platform-specific props
 *
 * @package @ideoz/ui
 */

import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Check, WandSparkles, Eye } from 'lucide-react';
import { Button } from '../atoms/button';
import { AppLayout } from '../layouts/AppLayout';
import { Header } from '../organisms/Header';
import { cn, Skeleton } from '@ideoz/ui';
import type { BillingPageFacade } from '@ideoz/core';
import Icon from '../molecules/Icon';
import UpgradeToSmartDialog from '../molecules/UpgradeToSmartDialog';
import CreditExhaustionModal from '../molecules/CreditExhaustionModal';
import DowngradeConfirmationModal from '../molecules/DowngradeConfirmationModal';
import CancelPendingDowngradeModal from '../molecules/CancelPendingDowngradeModal';
import PendingDowngradeBanner from '../molecules/PendingDowngradeBanner';

/**
 * BillingPageTemplate Props
 *
 * SIMPLIFIED with Facade Pattern!
 *
 * Before: 15+ individual props
 * After: 1 facade + 2-3 platform-specific props
 *
 * The facade encapsulates all shared logic following useMyProfilePageFacade pattern:
 * - State (isLoggedIn, user)
 * - Header configuration & user menu
 * - Navigation
 * - Billing data & actions
 */
export interface BillingPageTemplateProps {
  // ========== FACADE (all shared logic) ==========
  facade: BillingPageFacade;

  // ========== Platform-Specific Props ==========
  isWeb: boolean;

  // Web-specific
  authDialogState?: {
    isOpen: boolean;
    mode: 'login' | 'register';
    openLogin: () => void;
    openRegister: () => void;
    closeDialog: () => void;
    isLoading: boolean;
  };
}

export default function BillingPageTemplate({
  facade,
  isWeb,
  authDialogState,
}: BillingPageTemplateProps): JSX.Element {
  const { t } = useTranslation();
  // ========== Destructure Facade ==========
  const {
    state: { user, hasUpgradedPlan, showPlanDetails },
    header: {
      headerProps,
      userMenuOpen,
      onUserMenuClose,
      userMenuItems,
      userMenuSocialLinks,
      onRequestUpgrade,
      isRequestingUpgrade,
    },
    navigation,
    actions: { onUpgrade, onConfirmUpgrade, onCancelUpgrade, onDowngrade, onConfirmDowngrade, onCancelDowngrade, onCancelPendingDowngrade, onConfirmCancelPendingDowngrade, onCloseCancelPendingDowngradeModal, onRenewSubscription, onDownloadInvoice, onUpdatePaymentMethod },
    upgradeDialog: { isOpen: upgradeDialogOpen, isLoading: isUpgrading },
    downgradeDialog: { isOpen: downgradeDialogOpen, isLoading: isDowngrading },
    pendingDowngrade: { isPending: isPendingDowngrade, effectiveDate: pendingDowngradeDate, isCancelling: isCancellingDowngrade },
    cancelPendingDowngradeModal: { isOpen: cancelPendingDowngradeModalOpen, isLoading: isCancellingPendingDowngrade },
    creditExhaustionModal: { isOpen: creditExhaustionModalOpen, onClose: onCloseCreditExhaustionModal, availableCredits, totalCredits },
    subscriptionExpiration: { isExpiringWithin3Days, daysUntilExpiration },
    data: { currentPlan, creditBalances, monthlyCredits, purchasedCredits, nextResetDate, availablePlans, invoices, isLoadingPlans, isLoadingInvoices, paymentMethod, isLoadingPaymentMethod, billingCycle },
  } = facade;

  const primaryBalance = creditBalances?.[0];
  const currentPlanData = availablePlans.find((plan) => plan.isCurrent);
  const currentPlanId = currentPlanData?.id;
  const isFreePlan = currentPlan.name === 'Free' || currentPlanData?.name === 'Free';
  // Find Smart plan by name (since IDs are now UUIDs)
  const upgradeTargetPlan = availablePlans.find((plan) => plan.name === 'Smart')
    || availablePlans.find((plan) => !plan.isCurrent)
    || {
    id: currentPlanId,
    name: currentPlan.name,
    price: currentPlan.price,
    billingPeriod: currentPlan.billingPeriod,
  };
  const today = new Date();
  const upgradeEffectiveDate = `${today.getDate()}/${today.getMonth() + 1}/${today.getFullYear()} ${t('billing.immediately')}`;

  // Shared styles so every section reads as the same card and the page keeps a
  // single, consistent rhythm (spacing handled by the `space-y-6` wrapper).
  const cardClass =
    'rounded-2xl border border-gray-100 bg-surface p-6 dark:border-gray-700 dark:bg-gray-800';
  const cardTitleClass = 'text-sm font-semibold text-gray-900 dark:text-gray-100';
  const cardSubtitleClass = 'text-xs text-gray-500 dark:text-gray-400';
  const statCardClass =
    'rounded-lg border border-gray-200 px-4 py-3 dark:border-gray-700';
  const statLabelClass = 'text-xs text-gray-500 dark:text-gray-400';
  const statValueClass = 'text-lg font-semibold text-primary-700 dark:text-primary-300';

  // Guard against null user
  if (!user) {
    return (
      <div className="p-6 space-y-4" data-testid="billing-loading">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-72" />
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  return (
    <AppLayout
      currentPath="/billing"
      sidebar={facade.sidebar}
      user={{
        isLoggedin: !!user,
        name: user?.name,
        avatar: user?.avatar,
      }}
      userMenuProps={
        user
          ? {
            points: headerProps.points,
            pointsLabel: t('profile.pointsLabel'),
            menuItems: userMenuItems || [],
            socialLinks: userMenuSocialLinks || [],
            onRequestUpgrade,
            isRequestingUpgrade: isRequestingUpgrade || false,
            canRequestUpgrade: (headerProps.points || 0) <= 0,
            isWeb,
          }
          : undefined
      }
      subscriptionExpirationBanner={
        isExpiringWithin3Days
          ? {
            isVisible: true,
            daysUntilExpiration,
            onRenew: onRenewSubscription,
          }
          : undefined
      }
      isWeb={isWeb}
      testId="billing-page"
      className="data-testid-billing-page"
      height="screen"
      defaultSidebarCollapsed={false}
    >
      {/* Sticky top bar — matches MyProfileTemplate / StartupSubPageShell so
          Billing wears the same chrome as every other page. */}
      <Header
        {...headerProps}
        showBackButton
        showBackArrow={false}
        backText={t('billing.title')}
        isHasBorder
        className="sticky top-0 w-full"
        isWeb
      />

      <section className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 py-10" data-testid="billing-page">
        {/* Page Title */}
        <header className="flex flex-col gap-1">
          <h2 className="text-2xl text-start font-semibold text-gray-800 dark:text-gray-100">
            {t('billing.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">{t('billing.subtitle')}</p>
        </header>

        {/* Pending Downgrade Banner */}
        {isPendingDowngrade && pendingDowngradeDate && (
          <PendingDowngradeBanner
            isVisible={isPendingDowngrade}
            effectiveDate={pendingDowngradeDate}
            onCancelDowngrade={onCancelPendingDowngrade}
            isLoading={isCancellingDowngrade}
            testId="pending-downgrade-banner"
          />
        )}

        {/* Current Plan */}
        {showPlanDetails && (
        <section className={cardClass} data-testid="current-plan-section">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-3">
              <div>
                <p className={cardTitleClass}>{t('billing.currentPlan.label')}</p>
                <p className={cardSubtitleClass}>{t('billing.currentPlan.subtitle')}</p>
              </div>
              <div className="flex items-baseline gap-2">
                <p className="text-xl font-semibold text-primary-700 dark:text-primary-300">
                  {currentPlan.name}
                </p>
                <p className={statLabelClass}>
                  ${currentPlan.price}/{currentPlan.billingPeriod}
                </p>
              </div>
            </div>
            <a
              href="#plans-section"
              className="inline-flex h-9 shrink-0 items-center justify-center rounded-full border border-gray-300 px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/40"
            >
              {t('billing.currentPlan.viewPlans')}
            </a>
          </div>
        </section>
        )}

        {/* Usage & Credits */}
        <section className={cardClass} data-testid="usage-credits-section">
          <div className="mb-6">
            <p className={cardTitleClass}>{t('billing.usage.title')}</p>
            <p className={cardSubtitleClass}>{t('billing.usage.subtitle')}</p>
            <p className="mt-3 text-lg font-semibold text-primary-700 dark:text-primary-300">
              {t('billing.usage.creditsValue', {
                count: Math.max(0, (primaryBalance?.total ?? 0) - (primaryBalance?.used ?? 0)),
              })}
            </p>
          </div>

          {primaryBalance && (
            <div className="mb-6">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs text-gray-600 dark:text-gray-300">{t('billing.usage.available')}</p>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  {Math.max(0, (primaryBalance?.total ?? 0) - (primaryBalance?.used ?? 0))}/{primaryBalance?.total ?? 0}
                </p>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-full rounded-full bg-blue-light-600 transition-all duration-300"
                  style={{ width: `${primaryBalance?.percentage ?? 0}%` }}
                  data-testid="credit-bar-available"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className={statCardClass}>
              <p className={statLabelClass}>{t('billing.usage.monthly')}</p>
              <p className={statValueClass}>{monthlyCredits}</p>
            </div>
            <div className={statCardClass}>
              <p className={statLabelClass}>{t('billing.usage.nextReset')}</p>
              <p className={statValueClass}>{nextResetDate}</p>
            </div>
          </div>
        </section>

        {/* Free plan note */}
        {showPlanDetails && isFreePlan && (
          <section className="rounded-2xl border border-blue-light-200 bg-blue-light-50 p-4 dark:border-blue-light-500/40 dark:bg-blue-light-500/10" data-testid="plan-note">
            <p className="mb-1 text-xs font-semibold text-gray-700 dark:text-gray-200">
              {t('billing.freeNote.title')}
            </p>
            <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
              <Trans
                i18nKey="billing.freeNote.body"
                components={{
                  strong: <span className="font-semibold text-gray-800 dark:text-gray-100" />,
                }}
              />
            </p>
          </section>
        )}

        {/* Plans */}
        {showPlanDetails && (
        <section id="plans-section" data-testid="available-plans-section" className={cardClass}>
          <div className="space-y-1">
            <h2 className={cardTitleClass}>{t('billing.plans.title')}</h2>
            <p className={cardSubtitleClass}>{t('billing.plans.subtitle')}</p>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            {isLoadingPlans ? (
              // Loading skeleton for plans (2 skeleton cards)
              Array.from({ length: 2 }).map((_, index) => (
                <div
                  key={`plan-skeleton-${index}`}
                  className="rounded-xl border border-gray-200 bg-surface p-5 dark:border-gray-700 dark:bg-gray-800/40"
                  data-testid={`plan-skeleton-${index}`}
                >
                  {/* Header skeleton */}
                  <div className="flex items-start justify-between mb-2">
                    <Skeleton className="h-7 w-20" /> {/* Plan name */}
                    <Skeleton className="h-6 w-24 rounded-full" /> {/* Badge */}
                  </div>

                  {/* Description skeleton */}
                  <div className="space-y-2 mb-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-3/4" />
                  </div>

                  {/* Price skeleton */}
                  <Skeleton className="h-8 w-24 mb-3" />

                  {/* Features skeleton */}
                  <div className="space-y-3 mb-6">
                    {Array.from({ length: 6 }).map((_, featureIndex) => (
                      <div key={`feature-skeleton-${featureIndex}`} className="flex items-start gap-2">
                        <Skeleton className="h-4 w-4 rounded mt-0.5" />
                        <Skeleton className="h-4 w-full" />
                      </div>
                    ))}
                  </div>

                  {/* Button skeleton */}
                  <Skeleton className="h-9 w-full rounded-lg" />
                </div>
              ))
            ) : (
              availablePlans.map((plan) => (
                <div
                  key={plan.id}
                  className={cn(
                    'flex flex-col rounded-xl border bg-surface p-5 transition-colors dark:bg-gray-800/40',
                    plan.isCurrent
                      ? 'border-primary-300 ring-1 ring-primary-500/30 dark:border-primary-500/50'
                      : 'border-gray-200 dark:border-gray-700'
                  )}
                  data-testid={`plan-card-${plan.id}`}
                >
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{plan.name}</h3>
                    {plan.recommended && !plan.isCurrent && (
                      <span className="flex shrink-0 items-center gap-1 rounded-full border border-success-200 bg-success-50 px-2 py-1 text-xs font-medium text-success-700 dark:border-success-500/40 dark:bg-success-500/10 dark:text-success-300">
                        <Icon icon={WandSparkles} size={12} stroke="#17B26A" />
                        {t('billing.plans.recommended')}
                      </span>
                    )}
                    {plan.isCurrent && (
                      <span className="shrink-0 rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-700/40 dark:text-gray-300">
                        {t('billing.plans.current')}
                      </span>
                    )}
                  </div>

                  <div className="mb-4">
                    <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">{plan.description}</p>
                    <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">{plan.subDescription}</p>
                  </div>

                  <p className="mb-5 text-3xl font-semibold text-primary-700 dark:text-primary-300">
                    ${plan.price}
                    <span className="text-xs font-normal text-gray-500 dark:text-gray-400">/{plan.billingPeriod}</span>
                  </p>

                  <div className="mb-6 space-y-3">
                    {plan.features.map((feature) => (
                      <div key={feature.id} className="flex items-start gap-2" data-testid={`feature-${feature.id}`}>
                        <Check size={18} className="mt-0.5 shrink-0 text-green-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">{feature.name}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-auto pt-1">
                    {plan.isCurrent ? (
                      <Button variant="secondary-gray" disabled className="w-full" data-testid={`plan-button-${plan.id}`}>
                        {t('billing.plans.current')}
                      </Button>
                    ) : plan.name === 'Free' && hasUpgradedPlan ? (
                      <Button
                        onClick={() => onDowngrade?.()}
                        disabled={isPendingDowngrade}
                        variant="secondary-gray"
                        className="w-full"
                        data-testid={`plan-button-${plan.id}`}
                      >
                        {isPendingDowngrade ? t('billing.plans.downgradePending') : t('billing.plans.downgrade')}
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        onClick={() => onUpgrade?.(plan.id)}
                        className="w-full"
                        data-testid={`plan-button-${plan.id}`}
                      >
                        {t('billing.plans.upgrade')}
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
        )}

        {/* Only show for upgraded users */}
        {hasUpgradedPlan && (
          <>

            {/* Billing Cycle */}
            {showPlanDetails && billingCycle && (
              <section className={cardClass} data-testid="billing-cycle-section">
                <div className="mb-4">
                  <h3 className={cardTitleClass}>{t('billing.cycle.title')}</h3>
                  <p className={cardSubtitleClass}>{t('billing.cycle.subtitle')}</p>
                </div>
                <div className={cn(statCardClass, 'mb-4')}>
                  <p className={statLabelClass}>{t('billing.cycle.period')}</p>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{billingCycle.billingPeriod}</p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className={statCardClass}>
                    <p className={statLabelClass}>{t('billing.cycle.nextDate')}</p>
                    <p className="text-sm font-semibold text-primary-700 dark:text-primary-300">{billingCycle.nextBillingDate}</p>
                  </div>
                  <div className={statCardClass}>
                    <p className={statLabelClass}>{t('billing.cycle.nextCharge')}</p>
                    <p className="text-sm font-semibold text-primary-700 dark:text-primary-300">{billingCycle.nextCharge}</p>
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {/* Payment Method - Show when loading or when payment method exists */}
        {(isLoadingPaymentMethod || paymentMethod) && (
          <section className={cardClass} data-testid="payment-method-section">
            <div className="mb-6">
              <h3 className={cardTitleClass}>{t('billing.payment.title')}</h3>
              <p className={cardSubtitleClass}>{t('billing.payment.subtitle')}</p>
            </div>

            {isLoadingPaymentMethod ? (
              <div className={cn(statCardClass, 'flex items-center gap-4')}>
                <Skeleton className="w-12 h-8 rounded" />
                <div>
                  <Skeleton className="h-4 w-32 mb-1" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="ms-auto h-9 w-20 rounded-lg" />
              </div>
            ) : paymentMethod ? (
              <div className={cn(statCardClass, 'flex items-center gap-4')}>
                <img
                  src={`https://s3.us-east-1.amazonaws.com/app-test.ideoz.ai/credit+card+brand+logos/${paymentMethod.brand}.png`}
                  alt={paymentMethod.brand}
                  className="w-12 h-8 object-contain rounded"
                />
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{paymentMethod.maskedNumber}</p>
                  <p className={statLabelClass}>{t('billing.payment.expires', { date: paymentMethod.expiryDate })}</p>
                </div>
                <Button
                  variant="outline"
                  className="ms-auto h-9 px-3 font-semibold"
                  onClick={onUpdatePaymentMethod}
                >
                  {t('billing.payment.update')}
                </Button>
              </div>
            ) : null}
          </section>
        )}

        {/* Invoice History */}
        {showPlanDetails && (
        <section className={cardClass} data-testid="invoice-history-section">
          <div className="mb-6">
            <h3 className={cardTitleClass}>{t('billing.invoices.title')}</h3>
            <p className={cardSubtitleClass}>{t('billing.invoices.subtitle')}</p>
          </div>
          <div className="space-y-2">
            {isLoadingInvoices ? (
              // Loading skeleton for invoices
              Array.from({ length: 2 }).map((_, index) => (
                <div
                  key={`invoice-skeleton-${index}`}
                  className={cn(statCardClass, 'flex items-center justify-between')}
                >
                  <div>
                    <Skeleton className="h-3 w-24 mb-1" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-6 w-16 rounded-full" />
                    <Skeleton className="h-5 w-5 rounded" />
                  </div>
                </div>
              ))
            ) : invoices && invoices.length > 0 ? (
              invoices.map((invoice) => (
                <div key={invoice.id} className={cn(statCardClass, 'flex items-center justify-between')}>
                  <div>
                    <p className={statLabelClass}>{invoice.date}</p>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{invoice.amount}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={cn(
                      "flex gap-1 items-center text-xs font-medium px-2 py-1 rounded-full border",
                      invoice.displayStatus === 'paid'
                        ? "text-success-700 bg-success-50 border-success-200 dark:border-success-500/40 dark:bg-success-500/10 dark:text-success-300"
                        : invoice.displayStatus === 'pending'
                          ? "text-warning-700 bg-warning-50 border-warning-200 dark:border-warning-500/40 dark:bg-warning-500/10 dark:text-warning-300"
                          : "text-error-700 bg-error-50 border-error-200 dark:border-error-500/40 dark:bg-error-500/10 dark:text-error-300"
                    )}>
                      {invoice.displayStatus === 'paid'
                        ? t('billing.invoices.paid')
                        : invoice.displayStatus === 'pending'
                          ? t('billing.invoices.pending')
                          : t('billing.invoices.failed')}
                    </span>
                    <Button
                      variant="ghost"
                      className="p-0 h-auto text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-200"
                      onClick={() => onDownloadInvoice(invoice.id)}
                      data-testid={`download-invoice-${invoice.id}`}
                    >
                      <Icon icon={Eye} size={20} />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="py-6 text-center text-sm text-gray-400 dark:text-gray-500">{t('billing.invoices.empty')}</p>
            )}
          </div>
        </section>
        )}
      </section>

      {/* Upgrade to Smart Dialog */}
      <UpgradeToSmartDialog
        isOpen={upgradeDialogOpen}
        isLoading={isUpgrading}
        onClose={onCancelUpgrade}
        onConfirm={onConfirmUpgrade}
        currentPlan={{
          id: currentPlanId,
          name: currentPlan.name,
          price: currentPlan.price,
          billingPeriod: currentPlan.billingPeriod,
        }}
        newPlan={{
          id: upgradeTargetPlan.id,
          name: upgradeTargetPlan.name,
          price: upgradeTargetPlan.price,
          billingPeriod: upgradeTargetPlan.billingPeriod,
        }}
        effectiveDate={upgradeEffectiveDate}
      />

      {/* Downgrade Confirmation Modal */}
      <DowngradeConfirmationModal
        isOpen={downgradeDialogOpen}
        isLoading={isDowngrading}
        onClose={onCancelDowngrade}
        onConfirm={onConfirmDowngrade}
        currentPlanName={currentPlan.name}
        currentPlanPrice={currentPlan.price}
        currentPlanCredits={monthlyCredits}
        nextBillingDate={billingCycle?.nextBillingDate || 'N/A'}
      />

      {/* Cancel Pending Downgrade Modal */}
      <CancelPendingDowngradeModal
        isOpen={cancelPendingDowngradeModalOpen}
        isLoading={isCancellingPendingDowngrade}
        onClose={onCloseCancelPendingDowngradeModal}
        onConfirm={onConfirmCancelPendingDowngrade}
      />

      {/* Credit Exhaustion Modal */}
      <CreditExhaustionModal
        isOpen={creditExhaustionModalOpen}
        onClose={onCloseCreditExhaustionModal}
        availableCredits={availableCredits}
        totalCredits={totalCredits}
        onUpgrade={showPlanDetails ? onConfirmUpgrade : undefined}
        isLoading={isUpgrading}
        testId="credit-exhaustion-modal"
      />
    </AppLayout>
  );
}
